import React, { useState, useEffect, useRef } from 'react';
import './contact.css';
import { CiEdit } from 'react-icons/ci';
import axios from 'axios';
import Footer from '../../Component/NewFooter';

// const BASE_URL = "https://staging.pteshstudios.com";
const BASE_URL = "https://api.jeyshawnng.com";

const resolveSrc = (value, fallback = null) => {
  if (!value) return fallback;
  if (value instanceof File) return URL.createObjectURL(value);
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return fallback;
};

/* ─────────────────────────────────────────────
   LOCAL CACHE HELPERS (cache-first, DB-driven)
───────────────────────────────────────────── */
const CACHE_KEY = "jeyshawn_contact_data";

const getCachedData = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedData = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage full/unavailable — fail silently, page still works
  }
};

/* ─────────────────────────────────────────────
   EDIT MODAL
───────────────────────────────────────────── */
const EditModal = ({ isOpen, onClose, pageData, onRefresh }) => {
  // Draft list — each office has emails/phones as comma strings for editing
  const [offices,      setOffices]      = useState([]);
  const [heroImage,    setHeroImage]    = useState(null);
  const [sectionTitle, setSectionTitle] = useState("");
  const [heroLoading,  setHeroLoading]  = useState(false);
  const [savingIdx,    setSavingIdx]    = useState(null);
  const [deletingIdx,  setDeletingIdx]  = useState(null);
  const [reordering,   setReordering]   = useState(false);
  const [addLoading,   setAddLoading]   = useState(false);
  const [newOffice,    setNewOffice]    = useState({ name: "", address: "", emails: "", phones: "" });

  // Re-initialise only when modal transitions from closed → open
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened || !pageData) return;
    setSectionTitle(pageData.sectionTitle || "");
    setHeroImage(null);
    const sorted = [...(pageData.offices || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setOffices(sorted.map(o => ({
      _id:     o._id,
      name:    o.name    || "",
      address: o.address || "",
      emails:  (o.emails  || []).join(", "),
      phones:  (o.phones  || []).join(", "),
      order:   o.order ?? 0,
    })));
  }, [isOpen, pageData]);

  if (!isOpen) return null;

  const setField = (idx, field, val) =>
    setOffices(prev => prev.map((o, i) => i === idx ? { ...o, [field]: val } : o));

  /* ── Save hero / title ── */
  const handleSaveHero = async () => {
    setHeroLoading(true);
    try {
      const form = new FormData();
      form.append("sectionTitle", sectionTitle);
      if (heroImage instanceof File) form.append("heroImage", heroImage);
      await axios.put(`${BASE_URL}/api/contact`, form, { headers: { "Content-Type": "multipart/form-data" } });
      onRefresh();
    } catch (err) {
      console.error("Failed to save hero:", err);
    } finally {
      setHeroLoading(false);
    }
  };

  /* ── Save individual office ── */
  const handleSaveOffice = async (idx) => {
    setSavingIdx(idx);
    const o = offices[idx];
    try {
      const payload = {
        name:    o.name,
        address: o.address || null,
        emails:  o.emails ? o.emails.split(",").map(e => e.trim()).filter(Boolean) : [],
        phones:  o.phones ? o.phones.split(",").map(p => p.trim()).filter(Boolean) : [],
        order:   idx,
      };
      await axios.patch(`${BASE_URL}/api/contact/office/${o._id}`, payload);
      onRefresh();
    } catch (err) {
      console.error("Failed to save office:", err);
    } finally {
      setSavingIdx(null);
    }
  };

  /* ── Delete office ── */
  const handleDelete = async (idx) => {
    setDeletingIdx(idx);
    try {
      await axios.delete(`${BASE_URL}/api/contact/office/${offices[idx]._id}`);
      setOffices(prev => prev.filter((_, i) => i !== idx));
      onRefresh();
    } catch (err) {
      console.error("Failed to delete office:", err);
    } finally {
      setDeletingIdx(null);
    }
  };

  /* ── Reorder ── */
  const handleMove = async (idx, dir) => {
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= offices.length) return;
    const updated = [...offices];
    [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];
    setOffices(updated);
    setReordering(true);
    try {
      await axios.patch(`${BASE_URL}/api/contact/reorder`, {
        offices: updated.map((o, i) => ({ id: o._id, order: i })),
      });
      onRefresh();
    } catch (err) {
      console.error("Failed to reorder:", err);
    } finally {
      setReordering(false);
    }
  };

  /* ── Add office ── */
  const handleAdd = async () => {
    if (!newOffice.name.trim()) return;
    setAddLoading(true);
    try {
      const payload = {
        name:    newOffice.name,
        address: newOffice.address || null,
        emails:  newOffice.emails ? newOffice.emails.split(",").map(e => e.trim()).filter(Boolean) : [],
        phones:  newOffice.phones ? newOffice.phones.split(",").map(p => p.trim()).filter(Boolean) : [],
        order:   offices.length,
      };
      const { data } = await axios.post(`${BASE_URL}/api/contact/office`, payload);
      setNewOffice({ name: "", address: "", emails: "", phones: "" });
      // Append fresh office from response to local list
      const fresh = [...(data.data?.offices || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const last  = fresh[fresh.length - 1];
      if (last) {
        setOffices(prev => [...prev, {
          _id: last._id, name: last.name, address: last.address || "",
          emails: (last.emails || []).join(", "),
          phones: (last.phones || []).join(", "),
          order: last.order ?? prev.length,
        }]);
      }
      onRefresh();
    } catch (err) {
      console.error("Failed to add office:", err);
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div className="em-overlay" onClick={onClose}>
      <div className="em-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>

        <div className="em-header">
          <h2 className="em-title">Edit Contact Page</h2>
          <button className="em-close" onClick={onClose}>&times;</button>
        </div>

        <div className="em-body">

          {/* PAGE SETTINGS */}
          <p className="editor-section__title">Page Settings</p>
          <div className="em-expertise-card">
            <div className="em-field">
              <label className="em-label">Hero Image</label>
              <input type="file" accept="image/*" className="em-file-input"
                onChange={e => setHeroImage(e.target.files[0])} />
              {heroImage instanceof File
                ? <img src={URL.createObjectURL(heroImage)} alt="preview" className="em-file-preview" />
                : pageData?.heroImage
                  ? <img src={resolveSrc(pageData.heroImage)} alt="current" className="em-file-preview" />
                  : null
              }
            </div>
            <div className="em-field">
              <label className="em-label">Section Title</label>
              <input className="em-input" type="text" value={sectionTitle}
                onChange={e => setSectionTitle(e.target.value)} />
            </div>
            <button className="em-btn-save" onClick={handleSaveHero} disabled={heroLoading}>
              {heroLoading ? "Saving..." : "Save Settings"}
            </button>
          </div>

          <div className="em-divider" />

          {/* OFFICES */}
          <p className="editor-section__title">
            Offices / Contact Info {reordering && <span className="em-hint"> (Reordering...)</span>}
          </p>

          {offices.map((office, idx) => (
            <div className="em-expertise-card" key={office._id}>
              <div className="em-expertise-card-header">
                <span className="em-expertise-num">Office {String(idx + 1).padStart(2, "0")}</span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button className="em-remove-btn" onClick={() => handleMove(idx, "up")}   disabled={idx === 0 || reordering} title="Move up">↑</button>
                  <button className="em-remove-btn" onClick={() => handleMove(idx, "down")} disabled={idx === offices.length - 1 || reordering} title="Move down">↓</button>
                  <button className="em-remove-btn" onClick={() => handleDelete(idx)} disabled={deletingIdx === idx}>&times;</button>
                </div>
              </div>

              <div className="em-field">
                <label className="em-label">Name</label>
                <input className="em-input" type="text" value={office.name}
                  onChange={e => setField(idx, "name", e.target.value)} />
              </div>
              <div className="em-field">
                <label className="em-label">Address <span className="em-hint">(leave empty if none)</span></label>
                <textarea className="em-textarea" rows={2} value={office.address}
                  onChange={e => setField(idx, "address", e.target.value)} />
              </div>
              <div className="em-field">
                <label className="em-label">
                  Emails <span className="em-hint">(comma-separated)</span>
                  {office.emails && <span className="em-hint"> — current: {office.emails || "none"}</span>}
                </label>
                <input className="em-input" type="text"
                  placeholder="info@example.com, support@example.com"
                  value={office.emails}
                  onChange={e => setField(idx, "emails", e.target.value)} />
              </div>
              <div className="em-field">
                <label className="em-label">Phone Numbers <span className="em-hint">(comma-separated)</span></label>
                <input className="em-input" type="text"
                  placeholder="+234 8112 790 741"
                  value={office.phones}
                  onChange={e => setField(idx, "phones", e.target.value)} />
              </div>

              <button className="em-btn-save" onClick={() => handleSaveOffice(idx)} disabled={savingIdx === idx}>
                {savingIdx === idx ? "Saving..." : "Save Office"}
              </button>
            </div>
          ))}

          <div className="em-divider" />

          {/* ADD NEW */}
          <p className="editor-section__title">Add New Office / Contact Info</p>
          <div className="em-expertise-card">
            <div className="em-field">
              <label className="em-label">Name</label>
              <input className="em-input" type="text" placeholder="e.g. Corporate Headquarter"
                value={newOffice.name} onChange={e => setNewOffice(o => ({ ...o, name: e.target.value }))} />
            </div>
            <div className="em-field">
              <label className="em-label">Address</label>
              <textarea className="em-textarea" rows={2} placeholder="Full address..."
                value={newOffice.address} onChange={e => setNewOffice(o => ({ ...o, address: e.target.value }))} />
            </div>
            <div className="em-field">
              <label className="em-label">Emails <span className="em-hint">(comma-separated)</span></label>
              <input className="em-input" type="text" placeholder="info@example.com, other@example.com"
                value={newOffice.emails} onChange={e => setNewOffice(o => ({ ...o, emails: e.target.value }))} />
            </div>
            <div className="em-field">
              <label className="em-label">Phone Numbers <span className="em-hint">(comma-separated)</span></label>
              <input className="em-input" type="text" placeholder="+234 8112 790 741"
                value={newOffice.phones} onChange={e => setNewOffice(o => ({ ...o, phones: e.target.value }))} />
            </div>
            <button className="em-btn-save" onClick={handleAdd} disabled={addLoading || !newOffice.name.trim()}>
              {addLoading ? "Adding..." : "+ Add Office"}
            </button>
          </div>

        </div>

        <div className="em-footer">
          <button className="em-btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN PAGE
   Data comes only from the DB. On first ever
   visit (no cache) it loads from the API. On
   return visits it renders instantly from
   localStorage, then silently re-checks the DB
   in the background (and on an interval) and
   only updates the UI + cache if the DB data
   has actually changed.
───────────────────────────────────────────── */
const Contact = () => {
  const [formData,     setFormData]     = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [pageData,     setPageData]     = useState(() => getCachedData());
  const [loading,      setLoading]      = useState(() => !getCachedData());
  const [isModalOpen,  setIsModalOpen]  = useState(false);
  const [fetchError,   setFetchError]   = useState(null);
  const [showToast,    setShowToast]    = useState(false);

  const fetchData = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      setFetchError(null);
    }
    try {
      const { data } = await axios.get(`${BASE_URL}/api/contact`);
      const payload = data?.data ?? data;

      if (payload) {
        const cached = getCachedData();
        const changed = JSON.stringify(cached) !== JSON.stringify(payload);
        if (changed) {
          setPageData(payload);
          setCachedData(payload);
        }
      } else if (!isBackground && !getCachedData()) {
        setPageData(null);
      }
    } catch (err) {
      console.error("Failed to fetch contact data:", err);
      // Background syncs, or foreground loads when we already have cached
      // data, fail silently so the page keeps showing what it has.
      if (!isBackground && !getCachedData()) {
        setPageData(null);
        setFetchError(err.message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    const hasCache = !!getCachedData();
    fetchData(hasCache); // silent sync if we already have cached data to show

    const interval = setInterval(() => fetchData(true), 30000); // auto-check DB every 30s
    return () => clearInterval(interval);
  }, []);

  const displayOffices = pageData?.offices?.length > 0
    ? [...pageData.offices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const sectionTitle = pageData?.sectionTitle || "Get In Touch";

  const handleChange = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = e => {
    e.preventDefault();
    alert("Message sent! We will get back to you shortly.");
    setFormData({ name: "", email: "", phone: "", company: "", message: "" });
  };

  if (loading) {
    return (
      <div style={{ position:"fixed",inset:0,background:"#0a0a0a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:9999 }}>
        <div style={{ width:48,height:48,border:"3px solid rgba(255,255,255,0.1)",borderTop:"3px solid #fff",borderRadius:"50%",animation:"spin 0.8s linear infinite",marginBottom:20 }} />
        <p style={{ color:"rgba(255,255,255,0.5)",fontSize:13,letterSpacing:"0.1em",textTransform:"uppercase" }}>Loading</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <>
      <div className="img_carrier" style={{ position: "relative" }}>
        <button title="Edit page" onClick={() => setIsModalOpen(true)}
          style={{ position:"absolute",top:20,right:20,zIndex:10,background:"rgba(255,255,255,0.9)",border:"none",borderRadius:"50%",width:40,height:40,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.15)" }}>
          <CiEdit size={20} color="#333" />
        </button>
        {pageData?.heroImage && <img src={resolveSrc(pageData.heroImage)} alt="Contact hero" />}
      </div>

      {pageData && (
        <section className="contact-section" aria-labelledby="contact-title">
          <div className="contact-section__header">
            <h2 className="contact-section__title" id="contact-title">{sectionTitle}</h2>
            <hr className="contact-section__divider" />
          </div>
          <hr className="contact-section__rule" />

          <div className="contact-section__body">
            <div className="contact-info">
              {displayOffices.map((office, i) => (
                <div className="contact-info__office" key={office._id || office.name || i}>
                  <h3 className="contact-info__office-name">{office.name}</h3>
                  {office.address && <p className="contact-info__address">{office.address}</p>}
                  {office.phones?.length > 0 && (
                    <ul className="contact-info__phones">
                      {office.phones.map(phone => <li key={phone}>Tel: {phone}</li>)}
                    </ul>
                  )}
                  {office.emails?.length > 0 && (
                    <div className="contact-info__email">
                      <span className="contact-info__email-label">Email:</span>
                      {office.emails.map(email => <p key={email} className="contact-info__email-value">{email}</p>)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="contact-form-panel">
              <h3 className="contact-form__title">Send Message</h3>
              <form onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <input className="contact-form__input" type="text"  name="name"    placeholder="Name"    value={formData.name}    onChange={handleChange} required />
                  <input className="contact-form__input" type="email" name="email"   placeholder="Email"   value={formData.email}   onChange={handleChange} required />
                </div>
                <div className="contact-form__row">
                  <input className="contact-form__input" type="tel"  name="phone"   placeholder="Phone"   value={formData.phone}   onChange={handleChange} />
                  <input className="contact-form__input" type="text" name="company" placeholder="Company" value={formData.company} onChange={handleChange} />
                </div>
                <div className="contact-form__field">
                  <textarea className="contact-form__textarea" name="message" placeholder="Message" value={formData.message} onChange={handleChange} required />
                </div>
                <button type="submit" className="contact-form__submit">Send Message</button>
              </form>
            </div>
          </div>
        </section>
      )}

      <Footer />

      <EditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        pageData={pageData}
        onRefresh={() => fetchData(false)}
      />

      {showToast && (
        <div style={{ position:"fixed",bottom:20,right:20,background:"#dc2626",color:"#fff",padding:"16px 20px",borderRadius:8,display:"flex",alignItems:"center",gap:12,boxShadow:"0 4px 12px rgba(0,0,0,0.15)",zIndex:9999 }}>
          <div>
            <p style={{ fontWeight:600,margin:0 }}>Failed to load page data</p>
            <p style={{ fontSize:13,margin:"4px 0 0",opacity:0.9 }}>{fetchError}</p>
          </div>
          <button onClick={() => setShowToast(false)} style={{ background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16 }}>✕</button>
        </div>
      )}
    </>
  );
};

export default Contact;